/**
 * InsetConsoleController V2 - без querySelector
 * Управление инсет-системой через переданные рефы
 * Заменяет: querySelector('.degree-line') на прямые рефы
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import { CoordinateCalculator } from './CoordinateCalculator';
// Removed DegreeLine import - using ElementRenderingEngine instead

export interface InsetControllerRefs {
  linesOverlayGroup: SVGGElement;
  degreeLineElements: SVGLineElement[];
  yogaCircleElements?: SVGCircleElement[];
}

export interface InsetControllerConfig {
  debugMode?: boolean;
  enableYogaCircles?: boolean;
  animationDuration?: number;
}

/**
 * InsetConsoleController V2 - система управления инсетами без DOM-поиска
 * 
 * Особенности:
 * - Принимает рефы элементов напрямую
 * - Нет использования querySelector
 * - Реактивное обновление координат
 * - Интеграция с новой слоёвой системой
 */
export class InsetConsoleControllerV2 {
  private currentInsetRatio: number = 0.5;
  private refs: InsetControllerRefs | null = null;
  private config: InsetControllerConfig;
  private coordinateCalculator: CoordinateCalculator;
  
  constructor(config: InsetControllerConfig = {}) {
    this.config = {
      debugMode: false,
      enableYogaCircles: false,
      animationDuration: 300,
      ...config
    };
    
    this.coordinateCalculator = CoordinateCalculator.getInstance();
  }
  
  /**
   * Устанавливает рефы для управления элементами
   */
  setRefs(refs: InsetControllerRefs): void {
    this.refs = refs;
    
    if (this.config.debugMode) {
      devLog(`📐 InsetController V2: Refs set - ${refs.degreeLineElements.length} degree lines`);
    }
  }
  
  /**
   * Устанавливает соотношение инсета (0.0 - 1.0)
   */
  setInsetRatio(ratio: number): void {
    const clampedRatio = Math.max(0, Math.min(1, ratio));
    
    if (Math.abs(this.currentInsetRatio - clampedRatio) < 0.001) {
      return; // Нет значимых изменений
    }
    
    this.currentInsetRatio = clampedRatio;
    this.updateElementPositions();
    
    if (this.config.debugMode) {
      devLog(`📏 InsetController V2: Ratio updated to ${clampedRatio.toFixed(3)}`);
    }
  }
  
  /**
   * Устанавливает предустановку инсета
   */
  setPreset(preset: 'inner' | 'center' | 'outer'): void {
    const presetRatios = {
      inner: 0.0,
      center: 0.5,
      outer: 1.0
    };
    
    this.setInsetRatio(presetRatios[preset]);
    
    if (this.config.debugMode) {
      devLog(`🎯 InsetController V2: Preset '${preset}' applied (${presetRatios[preset]})`);
    }
  }
  
  /**
   * Возвращает текущее состояние
   */
  getStatus(): {
    currentRatio: number;
    hasRefs: boolean;
    elementCount: number;
    preset: string;
  } {
    const preset = this.currentInsetRatio === 0 ? 'inner' :
                  this.currentInsetRatio === 0.5 ? 'center' :
                  this.currentInsetRatio === 1.0 ? 'outer' : 'custom';

    return {
      currentRatio: this.currentInsetRatio,
      hasRefs: !!this.refs,
      elementCount: this.refs?.degreeLineElements.length || 0,
      preset
    };
  }

  /**
   * Returns current inset ratio (V1 API compatibility)
   */
  getInsetRatio(): number {
    return this.currentInsetRatio;
  }
  
  /**
   * Обновляет позиции всех элементов согласно текущему инсет-соотношению
   */
  private updateElementPositions(): void {
    if (!this.refs) {
      devWarn('⚠️ InsetController V2: No refs set, cannot update positions');
      return;
    }
    
    const { degreeLineElements, yogaCircleElements } = this.refs;
    
    // Обновляем degree lines
    degreeLineElements.forEach((lineElement, index) => {
      const house = parseInt(lineElement.getAttribute('data-house') || '0');
      const coordinates = this.calculateInsetLineCoordinates(house, this.currentInsetRatio);
      
      // Анимированное обновление координат
      this.animateLineUpdate(lineElement, coordinates);
    });
    
    // Обновляем yoga circles (если включены)
    if (this.config.enableYogaCircles && yogaCircleElements) {
      yogaCircleElements.forEach((circleElement, index) => {
        const house = parseInt(circleElement.getAttribute('data-house') || '0');
        const coordinates = this.calculateYogaCirclePosition(house, this.currentInsetRatio);
        
        this.animateCircleUpdate(circleElement, coordinates);
      });
    }
    
    if (this.config.debugMode) {
      devLog(`✅ InsetController V2: Updated ${degreeLineElements.length} elements`);
    }
  }
  
  /**
   * Вычисляет координаты degree line с учётом инсет-соотношения
   */
  private calculateInsetLineCoordinates(house: number, insetRatio: number): {
    x1: number; y1: number; x2: number; y2: number;
  } {
    const viewportSize = 700;
    const chartSize = 690;
    const cellSize = chartSize / 4;
    const offsetX = (viewportSize - chartSize) / 2;
    const offsetY = (viewportSize - chartSize) / 2;
    
    // Центр чарта
    const centerX = viewportSize / 2;
    const centerY = viewportSize / 2;
    
    // Позиции домов
    const housePositions = [
      { x: 0, y: 0 },                    // 0: 12-й дом
      { x: cellSize, y: 0 },             // 1: 1-й дом
      { x: cellSize * 2, y: 0 },         // 2: 2-й дом
      { x: cellSize * 3, y: 0 },         // 3: 3-й дом
      { x: cellSize * 3, y: cellSize },  // 4: 4-й дом
      { x: cellSize * 3, y: cellSize * 2 }, // 5: 5-й дом
      { x: cellSize * 3, y: cellSize * 3 }, // 6: 6-й дом
      { x: cellSize * 2, y: cellSize * 3 }, // 7: 7-й дом
      { x: cellSize, y: cellSize * 3 },  // 8: 8-й дом
      { x: 0, y: cellSize * 3 },         // 9: 9-й дом
      { x: 0, y: cellSize * 2 },         // 10: 10-й дом
      { x: 0, y: cellSize }              // 11: 11-й дом
    ];
    
    const pos = housePositions[house] || { x: 0, y: 0 };
    const houseX = pos.x + offsetX;
    const houseY = pos.y + offsetY;
    const houseCenterX = houseX + cellSize / 2;
    const houseCenterY = houseY + cellSize / 2;
    
    // Вычисляем координаты с учётом инсета
    const startX = houseCenterX + (houseX - houseCenterX) * (1 - insetRatio);
    const startY = houseCenterY + (houseY - houseCenterY) * (1 - insetRatio);
    
    const endX = centerX + (houseCenterX - centerX) * insetRatio;
    const endY = centerY + (houseCenterY - centerY) * insetRatio;
    
    return { x1: startX, y1: startY, x2: endX, y2: endY };
  }
  
  /**
   * Вычисляет позицию yoga circle
   */
  private calculateYogaCirclePosition(house: number, insetRatio: number): {
    cx: number; cy: number;
  } {
    const lineCoords = this.calculateInsetLineCoordinates(house, insetRatio);
    
    // Центр линии
    const cx = (lineCoords.x1 + lineCoords.x2) / 2;
    const cy = (lineCoords.y1 + lineCoords.y2) / 2;
    
    return { cx, cy };
  }
  
  /**
   * Анимированное обновление линии
   */
  private animateLineUpdate(element: SVGLineElement, coordinates: {
    x1: number; y1: number; x2: number; y2: number;
  }): void {
    const { animationDuration } = this.config;
    
    if (animationDuration && animationDuration > 0) {
      element.style.transition = `x1 ${animationDuration}ms ease, y1 ${animationDuration}ms ease, x2 ${animationDuration}ms ease, y2 ${animationDuration}ms ease`;
    }
    
    element.setAttribute('x1', coordinates.x1.toString());
    element.setAttribute('y1', coordinates.y1.toString());
    element.setAttribute('x2', coordinates.x2.toString());
    element.setAttribute('y2', coordinates.y2.toString());
  }
  
  /**
   * Анимированное обновление круга
   */
  private animateCircleUpdate(element: SVGCircleElement, coordinates: {
    cx: number; cy: number;
  }): void {
    const { animationDuration } = this.config;
    
    if (animationDuration && animationDuration > 0) {
      element.style.transition = `cx ${animationDuration}ms ease, cy ${animationDuration}ms ease`;
    }
    
    element.setAttribute('cx', coordinates.cx.toString());
    element.setAttribute('cy', coordinates.cy.toString());
  }
}

// Singleton instance для глобального доступа
let insetControllerInstance: InsetConsoleControllerV2 | null = null;

/**
 * Получает синглтон инстанс контроллера
 */
export function getInsetController(config?: InsetControllerConfig): InsetConsoleControllerV2 {
  if (!insetControllerInstance) {
    insetControllerInstance = new InsetConsoleControllerV2(config);
  }
  return insetControllerInstance;
}

// Console API для разработчиков (совместимость с существующим API)
if (typeof window !== 'undefined') {
  (window as any).insetV2 = {
    setInsetRatio: (ratio: number) => getInsetController().setInsetRatio(ratio),
    setPreset: (preset: 'inner' | 'center' | 'outer') => getInsetController().setPreset(preset),
    showStatus: () => {
      const status = getInsetController().getStatus();
      console.table(status);
      return status;
    },
    getCurrentRatio: () => getInsetController().getStatus().currentRatio
  };
}